import { useEffect, useState } from 'react';
import UnauthorizedHeader from '../UnauthorizedHeader';
import { getContractAppProcessUrl, isContractAppProcessEnabled } from '../../utils/featureFlags';
import { useCollapseAemContainer } from '../../hooks/useCollapseAemContainer';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../types/constants';

const APPLICATION_FORM_URL =
  import.meta.env.VITE_LEGACY_APPLICATION_URL ??
  'https://stage.hillspet.info/ShelterPortal/Application';

const SLOW_LOAD_TIMEOUT_MS = 15000;

const Apply = () => {
  const { t } = useTranslation();
  const contractAppProcessUrl = getContractAppProcessUrl();
  const shouldRedirect = isContractAppProcessEnabled() && Boolean(contractAppProcessUrl);
  const [isLoading, setIsLoading] = useState(true);
  const [isSlowLoad, setIsSlowLoad] = useState(false);

  useEffect(() => {
    document.title = 'Apply | Food Shelter Love';
  }, []);

  useCollapseAemContainer();

  // All /apply entry points (header, CMS, deep links, modals) land here —
  // redirect to the Contract Management portal when the feature flag is on.
  useEffect(() => {
    if (shouldRedirect && contractAppProcessUrl) {
      window.location.assign(contractAppProcessUrl);
    }
  }, [shouldRedirect, contractAppProcessUrl]);

  useEffect(() => {
    if (shouldRedirect || !isLoading) return;
    const timeout = setTimeout(() => setIsSlowLoad(true), SLOW_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [isLoading, shouldRedirect]);

  if (shouldRedirect) {
    return (
      <div className="min-h-dvh flex flex-col bg-white font-montserrat">
        <UnauthorizedHeader />
        <main className="flex-grow flex items-center justify-center">
          <p className="text-gray-600">{t(TranslationKeyEnum.RedirectingToApplication)}</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex flex-col bg-white font-montserrat">
      <UnauthorizedHeader />
      <main className="flex-grow flex flex-col">
        <div className="relative flex-grow min-h-[600px]">
          {isLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">
                  {isSlowLoad ? (
                    <>
                      {t(TranslationKeyEnum.ApplicationFormSlowLoad)}{' '}
                      <a
                        href={APPLICATION_FORM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        {t(TranslationKeyEnum.OpenInNewTab)}
                      </a>
                      .
                    </>
                  ) : (
                    t(TranslationKeyEnum.LoadingApplicationForm)
                  )}
                </p>
              </div>
            </div>
          )}
          <iframe
            src={APPLICATION_FORM_URL}
            title={t(TranslationKeyEnum.ApplicationFormTitle)}
            className="absolute inset-0 w-full h-full border-0"
            onLoad={() => setIsLoading(false)}
          />
        </div>
      </main>
    </div>
  );
};

export default Apply;
